import { trackLoggedOutRetailersDeepLinkBlogClick } from "@faire/web-api/events/loggedOutRetailers/click/deepLinkBlog";
import { ClientPlatformType } from "@faire/web-api/indigofair/data/ClientPlatformType";
import { Language } from "@faire/web-api/indigofair/languages/Language";
import brandApplication from "@faire/web-api/routes/apply";
import brands from "@faire/web-api/routes/brands";
import { useLocalization } from "@faire/web/common/localization/useLocalization";
import { getCurrentPath } from "@faire/web/common/Path";
import { Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";

import { HeaderCategoriesButton } from "@faire/retailer/components/Header/desktop/HeaderCategoriesDropdown/HeaderCategoriesButton";
import { useNavigationCardQuery } from "@faire/retailer/components/Header/desktop/HeaderCategoriesDropdown/useNavigationCardQuery";
import { HeaderDesktopLogo } from "@faire/retailer/components/Header/desktop/HeaderDesktopLogo";
import { LoginSection } from "@faire/retailer/components/Header/desktop/LoginSection";
import { useHeaderDesktopContent } from "@faire/retailer/components/Header/desktop/useHeaderDesktopContent";
import { HeaderCart } from "@faire/retailer/components/Header/HeaderCart";
import HeaderSearch from "@faire/retailer/components/Header/HeaderSearch";
import {
  HeaderAnchor,
  HeaderLine,
  HeaderLink,
  NAVIGATION_REDESIGN_MAX_HEADER_WIDTH,
} from "@faire/retailer/components/Header/styled";
import { LazyLanguageSelector } from "@faire/retailer/components/LanguageSelector/LazyLanguageSelector";
import { MarketNavElements } from "@faire/retailer/features/FaireMarkets/Navigation/MarketNavElements";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { blogPage } from "@faire/retailer/paths";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";
import { getSessionCountry } from "@faire/retailer/serialized-data/getSessionCountry";
import {
  useSettingGlobalNavigationRedesign,
  useAssignSettingGlobalNavigationRedesign,
} from "@faire/retailer/settings/useSettingGlobalNavigationRedesign";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useUserStore } from "@faire/retailer/stores/domain/UserStore";

export const HeaderDesktopContent = () => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const [shouldExpandSearchBar, setShouldExpandSearchBar] =
    useState<boolean>(false);
  const { isDesktopAndAbove } = useViewport();
  const { isMarketLinkVisible } = useHeaderDesktopContent();
  const assignSettingGlobalNavigationRedesign =
    useAssignSettingGlobalNavigationRedesign();

  // prefetch navigation card when navigation redesign is enabled
  useNavigationCardQuery(ClientPlatformType.CLIENT_DESKTOP);

  const isNavigationRedesignEnabled =
    useSettingGlobalNavigationRedesign() && isLoggedInRetailer;

  // This function reference must be stable for page scrolling
  const onSearchFocus = useCallback(
    (element: HTMLDivElement | null, isOnToggle?: boolean) => {
      if (isDesktopAndAbove && isOnToggle) {
        setShouldExpandSearchBar(
          !!(element && element.getBoundingClientRect()?.width < 300)
        );
      }
    },
    [isDesktopAndAbove]
  );

  // This function reference must be stable for page scrolling
  const onSearchBlur = useCallback(() => {
    setShouldExpandSearchBar(false);
  }, []);

  useEffect(() => {
    if (isLoggedInRetailer && isDesktopAndAbove) {
      assignSettingGlobalNavigationRedesign();
    }
  }, [
    assignSettingGlobalNavigationRedesign,
    isDesktopAndAbove,
    isLoggedInRetailer,
  ]);

  return (
    <Wrapper
      justify="center"
      align="center"
      $enableRedesign={isNavigationRedesignEnabled}
    >
      <HeaderDesktopLogo omitStyling={isNavigationRedesignEnabled} />
      {isNavigationRedesignEnabled ? (
        <>
          <Spacer size="2x" />
          <HeaderCategoriesButton />
          <Spacer size="2x" />
        </>
      ) : null}
      {isDesktopAndAbove ? (
        <HeaderSearch
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          inputId="top-search"
          inputDataTestId="searchBarInput"
        />
      ) : null}
      <Spacer width="2x" />
      {!shouldExpandSearchBar ? (
        <>
          <LanguageSelectorSection />
          {!isLoggedInRetailer ? <BlogEntryLink /> : null}
          {canAddToCart() ? null : (
            <HeaderLink
              to={
                getCurrentPath().pathname.includes(brands())
                  ? brandApplication()
                  : brands()
              }
            >
              <StrictLocalMsg defaultMessage="Sell on Faire" />
            </HeaderLink>
          )}
          {isMarketLinkVisible ? (
            <>
              <MarketNavElements />
              <HeaderLine />
              <Spacer width="1x" />
            </>
          ) : null}
        </>
      ) : null}
      <LoginSection />
      {canAddToCart() ? (
        <>
          <HeaderCart dataTestId="headerCartButton" />
          {isNavigationRedesignEnabled ? null : <Spacer width="3x" />}
        </>
      ) : null}
    </Wrapper>
  );
};

const LanguageSelectorSection = React.memo(() => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const {
    showLanguageSelector,
    showSEOLanguageSelector,
    availableLanguageSelectorLocales,
  } = useRetailerStore([
    "showLanguageSelector",
    "showSEOLanguageSelector",
    "availableLanguageSelectorLocales",
  ]);
  const { browserLevelLanguage, browserLevelLocaleKey } = useUserStore([
    "browserLevelLanguage",
    "browserLevelLocaleKey",
  ]);
  return !isLoggedInRetailer &&
    (showLanguageSelector || showSEOLanguageSelector) ? (
    <>
      <Suspense fallback={null}>
        <LazyLanguageSelector
          eventKey="logged-out-top"
          browserLanguage={browserLevelLanguage as Language}
          browserLocale={browserLevelLocaleKey}
          availableLocales={availableLanguageSelectorLocales}
          assignLocalizedUrl
          showCountries={getIsRobot()}
          sessionCountry={getSessionCountry()}
        />
      </Suspense>
      <HeaderLine />
    </>
  ) : null;
});

const BlogEntryLink = () => {
  const {
    intl: { locale },
  } = useLocalization();
  return (
    <HeaderAnchor
      href={`${blogPage(locale)}/`}
      data-test-id="blogEntryButton"
      onClick={() =>
        trackLoggedOutRetailersDeepLinkBlogClick("blog", "desktop")
      }
    >
      <StrictLocalMsg
        defaultMessage="Blog"
        description={{
          text: "title of link that leads to faire's blog page",
          img: "https://cdn.faire.com/fastly/4c5aaa52dda15b0270c374a7e3233e825bb47aecdc8bd8b533699e960093d3a5.png",
        }}
      />
    </HeaderAnchor>
  );
};

const Wrapper = styled(Row)<{ $enableRedesign?: boolean }>`
  width: 100%;

  ${({ $enableRedesign }) =>
    $enableRedesign
      ? css`
          max-width: ${NAVIGATION_REDESIGN_MAX_HEADER_WIDTH}px;
          margin: auto;

          ${responsiveMediaQueries.desktopAndAbove} {
            padding: ${getSpacing("2x")} ${getSpacing("6x")} 0;
          }
        `
      : ""}
`;
